// repossession-renter.ts
import { differenceInDays } from "date-fns";
import { IRenter, RENTER_STATUS, RECOVERY_REASON } from "@/types/renter.type";
import { getVisualRecords } from "@/common/services/vehicles.api";
import { IVehicleVisualRecord } from "@/types/vehicle-visual-record.type";
import { IRenterRentalHistory, RENTER_VEHICLE_LINK_STATUS, RENTER_LINK_TYPE } from "@/types/renter-rental-history";

/**
 * Dados de recolhimento/rescisão do cliente
 */
export interface RepossessionData {
  vehicleId: string;
  licensePlate: string;
  cancelDate: Date | null;
  repossessionDate: Date | null;
  optionRepossession: string;
  daysRemaining: number;
  rescissionDaysLeft: number;
  returnReason: string;
  visualRecords: IVehicleVisualRecord[];
  initialRegisters: Array<{ type: string; content: string; verified?: boolean }>;
}

/**
 * Dados financeiros do cancelamento
 */
export interface CancellationFinancialData {
  finePlanValue: number;
  dailyValue: number;
  valueWeeks: number;
  caucao: number;
  daysRemaining: number;
  planLabel: string;
  subscriptionEndDate: Date;
  subscriptionInitialDate: Date;
}

/**
 * Opções disponíveis para o modal de edição
 */
export interface EditBondOptions {
  canChangePlan: boolean;
  canRenewPlan: boolean;
  canCancelPlan: boolean;
  canFinishPlan: boolean;
  canUnfreezePlan: boolean;
  canAdditions: boolean;
  canChangeVehicle: boolean;
  canReactivate: boolean;
  isSvRecisionOnly: boolean;
  isDisabled: boolean;
  differeceDays: number;
}

const initialRegisters = [
  { type: 'Frente do veículo', content: '', verified: false },
  { type: 'Lateral direita do veículo', content: '', verified: false },
  { type: 'Lateral esquerda do veículo', content: '', verified: false },
  { type: 'Traseira do veículo', content: '', verified: false },
  { type: 'Odômetro', content: '', verified: false },
];

function isHistoryChangeVehicle(
  history?: { changeVehicle?: boolean; optionRepossesion?: string }
) {
  return history?.changeVehicle ?? history?.optionRepossesion === "VEHICLE_EXCHANGE";
}

/**
 * Obtém os dados de recolhimento/rescisão do cliente
 */
export async function getRepossessionData(renter: IRenter): Promise<RepossessionData> {
  const vehicleHistory = renter?.desactivationData?.vehicleHistory;

  let licensePlate = renter.activationData?.vehicle.licensePlate || '';
  let cancelDate: Date | null = null;
  let repossessionDate: Date | null = null;
  let optionRepossession = '';
  let vehiclesId = '';

  if (vehicleHistory && vehicleHistory.length > 0) {
    const lastHistory = vehicleHistory[vehicleHistory.length - 1];
    licensePlate = lastHistory.licensePlate || licensePlate;
    cancelDate = lastHistory.vehicleHistoryEnd ? new Date(lastHistory.vehicleHistoryEnd) : null;
    repossessionDate = cancelDate;
    optionRepossession = lastHistory.optionRepossesion || '';
    vehiclesId = lastHistory?.id || '';
  }

  const vehicleId = renter?.activationData?.vehicle?.id || vehiclesId;

  // Calcular dias restantes
  const dateCancelCalculate = cancelDate || new Date();
  const daysRemaining = Math.abs(
    differenceInDays(
      dateCancelCalculate,
      renter.activationData?.plan.subscriptionEndDate || new Date()
    )
  );

  // Calcular dias restantes para rescisão (10 dias após recolhimento)
  const rescissionDaysLeft = cancelDate
    ? 10 - differenceInDays(new Date(), cancelDate)
    : 0;

  // Buscar registros visuais
  let visualRecords: IVehicleVisualRecord[] = [];
  let returnReason = '';

  if (vehicleId && renter._id) {
    try {
      const searchData = {
        vehicleId: vehicleId,
        renterId: renter._id,
      };

      const { data, ok } = await getVisualRecords(searchData);

      if (ok && Array.isArray(data)) {
        const sortedData = data.sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ) as IVehicleVisualRecord[];

        const latestRecordWithReason = sortedData.find(r => r.returnReason);
        returnReason = latestRecordWithReason?.returnReason || '';
        visualRecords = sortedData;
      }
    } catch (error) {
      console.error('Erro ao buscar registros visuais:', error);
    }
  }

  // Pegar fotos de devolução ou usar registros iniciais
  const registers = visualRecords[0]?.photos?.devolutionPhotos || initialRegisters;

  return {
    vehicleId,
    licensePlate,
    cancelDate,
    repossessionDate,
    optionRepossession,
    daysRemaining,
    rescissionDaysLeft,
    returnReason,
    visualRecords,
    initialRegisters: registers,
  };
}

/**
 * Obtém os dados financeiros para cancelamento
 */
export function getCancellationFinancialData(renter: IRenter, daysRemaining: number): CancellationFinancialData {
  const dailyValue = parseFloat(
    renter?.activationData?.plan?.price?.replace('R$', '').replace(',', '.') || "0"
  );

  const finePlanValue = (dailyValue * daysRemaining) * 0.10;
  const valueWeeks = renter.activationData.plan?.value || 0;
  const caucao = renter.activationData.plan?.deposit || 0;

  return {
    finePlanValue,
    dailyValue,
    valueWeeks,
    caucao,
    daysRemaining,
    planLabel: renter.activationData?.plan?.label || '',
    subscriptionEndDate: renter.activationData?.plan.subscriptionEndDate || new Date(),
    subscriptionInitialDate: renter.activationData?.plan?.subscriptionInitialDate || new Date(),
  };
}

/**
 * Verifica quais opções estão disponíveis no modal de edição
 */
export function getEditBondOptions(renter: IRenter, cancelDate: Date | null): EditBondOptions {
  const planName = renter.activationData?.plan.label;
  const vehicleHistory = renter?.desactivationData?.vehicleHistory;
  const latestVehicleHistory = vehicleHistory?.[vehicleHistory.length - 1];
  const changeVehicle = isHistoryChangeVehicle(latestVehicleHistory);

  const isSvRecisionOnly =
    planName?.includes("SV") &&
    !changeVehicle &&
    renter.status === RENTER_STATUS.WaitingRescision;

  const differeceDays = cancelDate
    ? differenceInDays(new Date(), cancelDate) - 10
    : 0;

  const isDisabled =
    !planName ||
    (renter.status === RENTER_STATUS.Recall &&
      differeceDays > 0 &&
      differeceDays < 10);

  const showReactivatePlan =
    renter.status === RENTER_STATUS.Recall ||
    renter.status === RENTER_STATUS.Recision ||
    renter.status === RENTER_STATUS.Finished;

  return {
    canChangePlan: !isDisabled && !isSvRecisionOnly,
    canRenewPlan: !isDisabled && !isSvRecisionOnly,
    canCancelPlan: !!planName,
    canFinishPlan: !!planName && !isSvRecisionOnly,
    canUnfreezePlan: renter.status === RENTER_STATUS.Congelado && !isSvRecisionOnly,
    canAdditions: !isDisabled && !isSvRecisionOnly,
    canChangeVehicle: !isDisabled && !isSvRecisionOnly,
    canReactivate: showReactivatePlan && differeceDays < 10,
    isSvRecisionOnly,
    isDisabled,
    differeceDays,
  };
}

/**
 * Formata o status do cliente com informações de rescisão
 */
export function formatRenterStatus(renter: IRenter, cancelDate: Date | null): string {
  const STATUS_LABELS = {
    [RENTER_STATUS.Ativo]: "Ativo",
    [RENTER_STATUS.Incompleto]: "Incompleto",
    [RENTER_STATUS.PendentePagamento]: "Pendente de pagamento",
    [RENTER_STATUS.ProntoParaAprovacao]: "Pronto para aprovação",
    [RENTER_STATUS.ReservaPendentePagamento]: "Reserva pendente de pagamento",
    [RENTER_STATUS.ReservaPaga]: "Reserva paga",
    [RENTER_STATUS.Finished]: "Finalizado",
    [RENTER_STATUS.Recision]: "Recisão",
    [RENTER_STATUS.Recall]: "Recolhimento",
    [RENTER_STATUS.WaitingRescision]: "Aguardando recisão",
    [RENTER_STATUS.Congelado]: "Congelado",
  };

  if (renter.status === RENTER_STATUS.Recall && cancelDate) {
    const rescisionDaysLeft = 10 - differenceInDays(new Date(), cancelDate);
    return `${STATUS_LABELS[renter.status]} (10 dias) - faltam: ${rescisionDaysLeft} dias`;
  }

  return STATUS_LABELS[renter.status] || renter.status;
}

/**
 * Prepara todos os dados necessários para o fluxo de cancelamento
 */
export async function prepareCancellationData(renter: IRenter) {
  const repossessionData = await getRepossessionData(renter);
  const financialData = getCancellationFinancialData(renter, repossessionData.daysRemaining);
  const options = getEditBondOptions(renter, repossessionData.cancelDate);
  const status = formatRenterStatus(renter, repossessionData.cancelDate);

  return {
    repossession: repossessionData,
    financial: financialData,
    options,
    status,
    renter,
  };
}

/**
 * Converte dados de desactivationData para o formato de rentalHistory
 * Usado quando o cliente não possui histórico de rescisão mas tem dados de desativação
 */
export function convertDesactivationToRentalHistory(renter: IRenter): IRenterRentalHistory[] {
  const desactivationData = renter.desactivationData;

  // Se não tem dados de desativação, retorna array vazio
  if (!desactivationData || !desactivationData.vehicleHistory || desactivationData.vehicleHistory.length === 0) {
    return [];
  }

  const vehicleHistory = desactivationData.vehicleHistory;

  // Pega o último registro (mais recente)
  const lastVehicle = vehicleHistory[vehicleHistory.length - 1];

  // Monta o objeto no formato IRenterRentalHistory
  const rentalHistoryItem: IRenterRentalHistory = {
    _id: renter._id || '',
    renterId: renter._id || '',
    renterName: renter.name || '',
    vehicleId: lastVehicle.id || '',
    licensePlate: lastVehicle.licensePlate || '',
    partnerId: lastVehicle.partnerId,
    partnerCompanyName: '',

    photosSaved: {
      photosLinked: false,
      photosUnlinked: false
    },

    vehicleVisualRecordId: undefined,
    repossessionId: undefined,

    // Motivo do recolhimento
    reason: (lastVehicle.optionRepossesion as RECOVERY_REASON) || RECOVERY_REASON.PLAN_CANCELLATION,
    notes: '',

    status: RENTER_VEHICLE_LINK_STATUS.REPOSSESSED,
    typeLink: RENTER_LINK_TYPE.REPOSSESSION,

    historyAction: [
      {
        type: RENTER_LINK_TYPE.REPOSSESSION,
        date: lastVehicle.vehicleHistoryEnd || new Date(),
        autor: '',
      }
    ],

    companyId: renter.companyId,

    // Datas importantes
    unlinkedAt: lastVehicle.vehicleHistoryEnd || new Date(),
    createdAt: lastVehicle.vehicleHistoryInitial || new Date(),
    updatedAt: lastVehicle.vehicleHistoryEnd || new Date(),

    // Peças avariadas (vazio por padrão, pois não está em desactivationData)
    damagedParts: [],
    withoutPhotos: false,
    returnReason: ""
  };

  return [rentalHistoryItem];
}

/**
 * Obtém o histórico de locação com fallback para desactivationData
 * Esta função deve ser usada no useRescission para garantir dados sempre
 */
export async function getRentalHistoryWithFallback(
  renter: IRenter,
  rentalHistoryFromAPI: IRenterRentalHistory[]
): Promise<IRenterRentalHistory[]> {

  // Se tem dados da API, usa eles
  if (rentalHistoryFromAPI && rentalHistoryFromAPI.length > 0) {
    return rentalHistoryFromAPI;
  }

  // Se não tem, tenta converter de desactivationData
  const convertedHistory = convertDesactivationToRentalHistory(renter);

  if (convertedHistory.length > 0) {
    return convertedHistory;
  }

  // Se não tem nada, retorna array vazio
  return [];
}

/**
 * Obtém dados financeiros do último plano (ativo ou desativado)
 */
export function getLastPlanFinancialData(renter: IRenter) {
  // Tenta primeiro do activationData (plano ativo)
  if (renter.activationData?.plan) {
    const plan = renter.activationData.plan;
    return {
      label: plan.label || '',
      value: plan.value || 0,
      rawValue: plan.rawValue || 0,
      price: plan.price || '',
      deposit: plan.deposit || 0,
      subscriptionInitialDate: plan.subscriptionInitialDate,
      subscriptionEndDate: plan.subscriptionEndDate,
    };
  }

  // Se não, pega do desactivationData (último plano desativado)
  const planHistory = renter.desactivationData?.planHistory;
  if (planHistory && planHistory.length > 0) {
    const lastPlan = planHistory[planHistory.length - 1];
    return {
      label: lastPlan.label || '',
      value: lastPlan.value || 0,
      rawValue: lastPlan.rawValue || 0,
      price: lastPlan.price || '',
      deposit: lastPlan.deposit || 0,
      subscriptionInitialDate: lastPlan.subscriptionInitialDate,
      subscriptionEndDate: lastPlan.subscriptionEndDate,
    };
  }

  // Fallback vazio
  return {
    label: 'Plano não encontrado',
    value: 0,
    rawValue: 0,
    price: 'R$ 0,00',
    deposit: 0,
    subscriptionInitialDate: new Date(),
    subscriptionEndDate: new Date(),
  };
}

/**
 * Obtém o último veículo (ativo ou desativado)
 */
export function getLastVehicleData(renter: IRenter) {
  // Tenta primeiro do activationData (veículo ativo)
  if (renter.activationData?.vehicle) {
    const vehicle = renter.activationData.vehicle;
    return {
      id: vehicle.id || '',
      licensePlate: vehicle.licensePlate || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      color: vehicle.color || '',
    };
  }

  // Se não, pega do desactivationData (último veículo desativado)
  const vehicleHistory = renter.desactivationData?.vehicleHistory;
  if (vehicleHistory && vehicleHistory.length > 0) {
    const lastVehicle = vehicleHistory[vehicleHistory.length - 1];
    return {
      id: lastVehicle.id || '',
      licensePlate: lastVehicle.licensePlate || '',
      brand: lastVehicle.brand || '',
      model: lastVehicle.model || '',
      color: lastVehicle.color || '',
    };
  }

  // Fallback vazio
  return {
    id: '',
    licensePlate: 'N/A',
    brand: 'N/A',
    model: 'N/A',
    color: 'N/A',
  };
}
